/*
 * What a prescription actually sent, for the list rather than the detail view (KIN-1966).
 *
 * Admin staff use that list to answer "has my prescription been sent?", and the follow-up is
 * always "what was sent, and for how long" — a doctor sending one month instead of six is the
 * thing they need to spot, and today it is invisible until each prescription is opened.
 *
 * Every field here is nullable or zero in real data: `strength: null`, `duration: null`,
 * `quantity: 0`, `repeat: 0`. Nothing is rendered for an absent part — "0 days" and "undefined"
 * are worse than silence, because they read as a fact about the prescription.
 */

const hasText = (value) => typeof value === "string" && !!value.trim();

/** Trade name leads, because it is what a patient reads off the box and asks about. */
export const formatMedicationName = (medication) => {
  const tradeName = hasText(medication?.tradeName) ? medication.tradeName.trim() : null;
  const genericName = hasText(medication?.genericName)
    ? medication.genericName.trim()
    : null;

  return tradeName ?? genericName ?? null;
};

/** The generic name, but only when it is not simply repeating the name already shown. */
export const formatMedicationSecondaryName = (medication) => {
  const primary = formatMedicationName(medication);
  const genericName = hasText(medication?.genericName)
    ? medication.genericName.trim()
    : null;

  if (!genericName || !primary) return null;

  return genericName.toLowerCase() === primary.toLowerCase() ? null : genericName;
};

/**
 * Dose, how long, and how many repeats — joined into the one line the list has room for.
 *
 * A duration of 0 is dropped rather than shown: the backend uses it for "not stated", and
 * "0 days" would assert a course that ends immediately. `0 repeats` is dropped for the same
 * reason — a prescription with no repeats says nothing by saying nothing.
 */
export const formatMedicationDetail = (medication) => {
  const parts = [];

  const dose = [medication?.dosage, medication?.strength, medication?.form]
    .filter(hasText)
    .map((part) => part.trim());

  if (dose.length) parts.push(dose.join(" "));

  const duration = Number(medication?.duration);

  if (Number.isFinite(duration) && duration > 0) {
    const unit = hasText(medication?.durationFormat)
      ? medication.durationFormat.trim()
      : "days";

    parts.push(`${duration} ${unit}`);
  }

  const repeat = Number(medication?.repeat);

  if (Number.isFinite(repeat) && repeat > 0) {
    parts.push(`${repeat} ${repeat === 1 ? "repeat" : "repeats"}`);
  }

  return parts.join(" · ");
};

/**
 * The medications on a prescription, as the list shows them.
 *
 * Entries with no name at all are dropped — a row reading only "28 days" tells a clinician
 * nothing and takes the place of one that would.
 */
export const summarisePrescribedMedications = (prescription) =>
  (prescription?.medications ?? [])
    .map((medication) => ({
      key: medication?.id ?? formatMedicationName(medication),
      name: formatMedicationName(medication),
      secondaryName: formatMedicationSecondaryName(medication),
      detail: formatMedicationDetail(medication),
    }))
    .filter((entry) => !!entry.name);
